/**
 * NotAuthorized
 *
 * Pure presentational component — renders the "not authorized" message and a
 * sign-out button. Accepts an optional onSignOut callback so it can be used
 * both in tests (without AuthProvider) and inside NotAuthorizedPage (with it).
 */

interface NotAuthorizedProps {
  onSignOut?: () => void;
  /** Signed-in identity + raw ID-token claims, for the access-diagnostic panel. */
  signedInAs?: string;
  claims?: Record<string, unknown>;
}

export function NotAuthorized({ onSignOut, signedInAs, claims }: NotAuthorizedProps = {}) {
  const claimKeys = claims ? Object.keys(claims).sort() : [];
  const roleish = claims
    ? Object.entries(claims).filter(([k]) => k.toLowerCase().includes('role'))
    : [];
  return (
    <main className="flex-1 flex flex-col items-center justify-center min-h-screen bg-zinc-950">
      <div className="text-center max-w-md px-6">
        <h1 className="text-2xl font-bold text-zinc-100 mb-3">Not Authorized</h1>
        <p className="text-zinc-400 text-sm mb-6">
          You are signed in, but your account does not have the{' '}
          <code className="text-violet-400 bg-zinc-900 px-1 py-0.5 rounded text-xs">Admin</code>{' '}
          role required to access this site. Contact your administrator to request access.
        </p>
        {signedInAs && (
          <div className="text-left bg-zinc-900 border border-zinc-800 rounded-lg p-4 mb-6 text-xs text-zinc-400">
            <p className="mb-2">
              Signed in as <span className="text-zinc-200">{signedInAs}</span>
            </p>
            <p className="mb-1">
              Role claims in your token:{' '}
              {roleish.length > 0 ? (
                roleish.map(([k, v]) => (
                  <code key={k} className="text-violet-400 bg-zinc-950 px-1 rounded mr-1">
                    {k}={String(v)}
                  </code>
                ))
              ) : (
                <span className="text-amber-400">none</span>
              )}
            </p>
            {claimKeys.length > 0 && (
              <p className="break-words">
                All claim names: <span className="text-zinc-500">{claimKeys.join(', ')}</span>
              </p>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={onSignOut}
          className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm rounded-lg transition-colors min-h-[48px]"
        >
          Sign out
        </button>
      </div>
    </main>
  );
}
